import { useEffect } from 'react';

const ASSET_TYPE_EXAMPLES = {
  stance: '例: 「勢いよりも、続けやすさを優先する」',
  framework: '例: 「悩みを分解 → 比較 → 決定の順で整理する」',
  preference: '例: 「朝に一人で考える時間があると調子がいい」',
  knowledge: '例: 「このテーマでは A と B をこの条件で使い分ける」',
  todo: '例: 「今週中に候補3件を比較する」',
  project: '例: 「来月までに発信の型を作って運用する」',
  interest: '例: 「なぜ自分はこのテーマに繰り返し惹かれるのか」',
};

function getStatusGuidance(statusLabel) {
  if (statusLabel === '使用中') {
    return '保存後はすぐ一覧で使う前提の状態になります。必要に応じて後から保留や完了に変えられます。';
  }
  return '保存後は下書きで始まります。迷う部分はあとで整えれば大丈夫です。';
}

export default function AssetTypeModal({
  isAssetTypeModalOpen,
  setIsAssetTypeModalOpen,
  assetizingInsight,
  assetTypeDraft,
  setAssetTypeDraft,
  assetTitleDraft,
  setAssetTitleDraft,
  assetBodyDraft,
  setAssetBodyDraft,
  ASSET_TYPES,
  getAssetTypeLabel,
  getAssetStatusLabel,
  getInitialAssetStatus,
  ASSET_TYPE_DESCRIPTIONS,
  handlePromoteInsightToConcept,
  assetDuplicateCandidates,
  handleMergeInsightIntoAsset,
}) {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsAssetTypeModalOpen(false);
      }
    };
    if (isAssetTypeModalOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isAssetTypeModalOpen, setIsAssetTypeModalOpen]);

  if (!isAssetTypeModalOpen || !assetizingInsight) return null;

  const initialStatusLabel = getAssetStatusLabel(getInitialAssetStatus(assetTypeDraft));
  const selectedTypeExample = ASSET_TYPE_EXAMPLES[assetTypeDraft] || ASSET_TYPE_EXAMPLES.knowledge;

  return (
    <div className="fixed inset-0 z-[160] bg-black/60 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) setIsAssetTypeModalOpen(false); }}>
      <form onSubmit={handlePromoteInsightToConcept} className="w-full max-w-xl bg-gray-800 border border-gray-700 rounded-lg shadow-xl p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-white">Asset化</h2>
          <button aria-label="閉じる" type="button" onClick={() => setIsAssetTypeModalOpen(false)} className="text-gray-400 hover:text-white text-xl leading-none">
            ×
          </button>
        </div>

        <div className="rounded-xl border border-emerald-500/30 bg-emerald-950/20 p-3 space-y-1.5">
          <p className="text-sm font-bold text-emerald-200">迷ったら、タイプだけ確認してそのまま保存でOKです。</p>
          <p className="text-xs text-emerald-100/80 leading-relaxed">タイトルと内容にはAI案を入れています。必要なときだけ編集し、細部はあとで整えてください。</p>
        </div>

        {(assetizingInsight?.suggestedTargetType === 'todo' || assetizingInsight?.suggestedTargetType === 'project') && (
          <p className="rounded-lg border border-blue-500/20 bg-blue-950/20 px-3 py-2 text-[11px] text-blue-100/80">
            短期メモなら閉じてNoteのまま残すだけでもOKです。残したい行動だけ ToDo / Project として保存してください。
          </p>
        )}
        
        <div className="space-y-3">
          <div className="space-y-1">
            <label htmlFor="asset-title" className="block text-xs font-medium text-gray-400">Assetタイトル（必要なら編集）</label>
            <input
              id="asset-title"
              type="text"
              value={assetTitleDraft}
              onChange={(e) => setAssetTitleDraft(e.target.value)}
              placeholder="空でもAI案のまま保存されます"
              className="w-full bg-gray-700 border border-gray-600 text-white text-sm rounded-lg px-3 py-2 outline-none focus:border-emerald-500 transition-all"
            />
          </div>
          <div className="space-y-1">
            <label htmlFor="asset-body" className="block text-xs font-medium text-gray-400">内容（必要なら編集）</label>
            <textarea
              id="asset-body"
              value={assetBodyDraft}
              onChange={(e) => setAssetBodyDraft(e.target.value)}
              placeholder="空でもAI案のまま保存されます"
              className="w-full bg-gray-700 border border-gray-600 text-white text-sm rounded-lg px-3 py-2 outline-none focus:border-emerald-500 transition-all h-32 resize-none"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-xs font-medium text-gray-400">Assetタイプを選択</label>
          <div className="grid grid-cols-2 gap-2">
            {ASSET_TYPES.map(type => (
              <button
                key={type}
                type="button"
                onClick={() => setAssetTypeDraft(type)}
                className={`text-left p-2 rounded-lg border transition-all ${assetTypeDraft === type ? 'bg-emerald-900/30 border-emerald-500 shadow-sm' : 'bg-gray-700 border-gray-600 hover:border-gray-500'}`}
              >
                <p className={`text-xs font-bold ${assetTypeDraft === type ? 'text-emerald-300' : 'text-white'}`}>{getAssetTypeLabel(type)}</p>
                <p className="text-[9px] text-gray-500 mt-0.5 leading-tight line-clamp-2">{ASSET_TYPE_DESCRIPTIONS[type]}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-gray-700 bg-gray-900/40 p-3 space-y-2">
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-emerald-400">選択中のタイプ</p>
            <p className="text-sm font-bold text-white mt-1">{getAssetTypeLabel(assetTypeDraft)}</p>
          </div>
          <p className="text-xs text-gray-300 leading-relaxed">{ASSET_TYPE_DESCRIPTIONS[assetTypeDraft]}</p>
          <p className="text-xs text-gray-400 leading-relaxed">{selectedTypeExample}</p>
          <div className="pt-1 border-t border-gray-700/70">
            <p className="text-[10px] font-black uppercase tracking-widest text-sky-400">保存後の状態</p>
            <p className="text-sm font-bold text-white mt-1">{initialStatusLabel}</p>
            <p className="text-xs text-gray-400 leading-relaxed mt-1">{getStatusGuidance(initialStatusLabel)}</p>
          </div>
        </div>

        {assetDuplicateCandidates?.length > 0 && (
          <div className="space-y-2">
            <label className="block text-[10px] font-black uppercase tracking-widest text-amber-500">類似した既存のAsset</label>
            <div className="space-y-2">
              {assetDuplicateCandidates.map(asset => (
                <div key={asset.id} className="flex items-center justify-between p-2 bg-amber-950/20 border border-amber-500/30 rounded-lg gap-3">
                  <div className="min-w-0">
                    <p className="text-xs text-white font-bold truncate">📄 {asset.title}</p>
                    <p className="text-[9px] text-amber-400/70">{getAssetTypeLabel(asset.type)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleMergeInsightIntoAsset(assetizingInsight, asset)}
                    className="flex-shrink-0 bg-amber-600 hover:bg-amber-500 text-white text-[10px] font-bold px-2 py-1 rounded transition-colors"
                  >
                    これに紐付ける
                  </button>
                </div>
              ))}
            </div>
            <p className="text-[9px] text-gray-500 leading-tight">※ 同じ内容のAssetがすでにある場合は、新規作成せず既存のものに紐付けることができます。</p>
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <button type="button" onClick={() => setIsAssetTypeModalOpen(false)} className="flex-1 bg-gray-700 text-white py-2 rounded text-sm font-bold">
            キャンセル
          </button>
          <button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white py-2 rounded text-sm font-bold shadow-lg shadow-emerald-900/20 transition-all active:scale-95">
            この内容でAsset化
          </button>
        </div>
      </form>
    </div>
  );
}
